import { Dialog, DialogContent, DialogTrigger } from 'shared/components/ui/dialog';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useFileOperations } from '@dev-streetbeat/chat/react';

import type { File } from '@dev-streetbeat/chat/types';

import { DownloadableArtifact } from './DownloadableArtifact';
import { svgImage } from './svgImage';

import styles from './SvgFileArtifact.module.scss';

type Props = {
  file: File;
  onFinishLoading?: () => void;
};

export const SvgFileArtifact = forwardRef<{ expandSvg: () => void }, Props>(({ file, onFinishLoading }, ref) => {
  useImperativeHandle(ref, () => ({
    expandSvg: () => {
      openDialog();
    },
    downloadSvg: () => {
      download();
    },
  }));

  const dialogTriggerRef = useRef(null);

  const openDialog = () => {
    dialogTriggerRef.current?.click();
  };

  // The svg is shown as an image from a data: URL, never inserted into the page: an image runs no script, and a
  // data: URL has no origin (a blob: URL would carry the app's, so «open image in new tab» would run it in-origin).
  const [url, setUrl] = useState('');
  const [aspect, setAspect] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  const { downloadFile } = useFileOperations();

  useEffect(() => {
    let cancelled = false;
    downloadFile(file.id).then(async (response) => {
      const svgText = await response.text();
      if (cancelled) return;
      const image = svgImage(svgText);
      setAspect(image.aspect);
      setUrl(image.url);
      onFinishLoading?.();
    });
    return () => {
      cancelled = true;
    };
  }, [file.id, onFinishLoading]);

  const download = async () => {
    const fileName = file.name;
    const fileId = file.id;

    const response = await downloadFile(fileId);

    const link = document.createElement('a');
    link.href = URL.createObjectURL(response);
    link.download = fileName;
    link.click();
  };

  if (!url) {
    return null;
  }

  // An svg the browser still cannot show as an image is offered as a download instead of a broken image.
  if (failed) {
    return <DownloadableArtifact file={file} />;
  }

  return (
    <>
      <div className={styles.svgContainer}>
        <div className="svg-attachment-scroll-margin"></div>
        <div className={styles.svgOverlayActions}>
          <ExpandDialog triggerRef={dialogTriggerRef} title={file.name}>
            <img src={url} alt={file.name} />
          </ExpandDialog>
        </div>
        <div
          id={file.id.toString()}
          className={styles.svgContent}
          style={aspect ? { paddingBottom: `${aspect * 100}%` } : undefined}
        >
          <img
            src={url}
            alt={file.name}
            className={aspect ? styles.svgInner : styles.svgFlow}
            onError={() => setFailed(true)}
          />
        </div>
      </div>
    </>
  );
});

const ExpandDialog = ({ children, triggerRef, title }) => {
  return (
    <Dialog>
      <DialogTrigger ref={triggerRef}></DialogTrigger>
      <DialogContent className={styles.dialogContentWrapper} title={title}>
        <div className={styles.dialogContent}>{children}</div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpandDialog;
